import type {
  Footnote,
  Paragraph,
  StudyHighlight,
  StudyNote,
} from '../../data/schema';

export interface TextSelection {
  startOffset: number;
  endOffset: number;
  text: string;
}

export interface ParagraphActions {
  onFootnote(footnote: Footnote, anchor: HTMLElement): void;
  onShare(paragraph: Paragraph, anchor: HTMLElement): void;
  onHighlight(
    paragraph: Paragraph,
    selection: TextSelection,
    anchor: HTMLElement,
  ): void;
  onNote(paragraph: Paragraph, note: StudyNote | undefined, anchor: HTMLElement): void;
}

export interface ParagraphView {
  element: HTMLElement;
  updateStudyState(highlights: StudyHighlight[], note: StudyNote | undefined): void;
}

function selectedText(
  textRoot: HTMLElement,
  paragraph: Paragraph,
): TextSelection {
  const selection = document.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return {
      startOffset: 0,
      endOffset: paragraph.text.length,
      text: paragraph.text,
    };
  }
  const range = selection.getRangeAt(0);
  if (
    !textRoot.contains(range.startContainer) ||
    !textRoot.contains(range.endContainer)
  ) {
    return {
      startOffset: 0,
      endOffset: paragraph.text.length,
      text: paragraph.text,
    };
  }

  const prefix = range.cloneRange();
  prefix.selectNodeContents(textRoot);
  prefix.setEnd(range.startContainer, range.startOffset);
  const startOffset = prefix.toString().length;
  const text = range.toString();
  const endOffset = startOffset + text.length;
  if (!text.trim() || endOffset > paragraph.text.length) {
    return {
      startOffset: 0,
      endOffset: paragraph.text.length,
      text: paragraph.text,
    };
  }
  return { startOffset, endOffset, text };
}

function renderHighlightedText(
  root: HTMLElement,
  paragraph: Paragraph,
  highlights: StudyHighlight[],
): void {
  root.replaceChildren();
  let cursor = 0;
  const ordered = highlights
    .filter(
      (highlight) =>
        highlight.paragraphId === paragraph.id &&
        highlight.startOffset >= 0 &&
        highlight.endOffset <= paragraph.text.length,
    )
    .sort((first, second) => first.startOffset - second.startOffset);

  for (const highlight of ordered) {
    if (highlight.startOffset < cursor) continue;
    root.append(
      document.createTextNode(paragraph.text.slice(cursor, highlight.startOffset)),
    );
    const mark = document.createElement('mark');
    mark.className = `study-highlight study-highlight--${highlight.color}`;
    mark.dataset.highlightId = String(highlight.id);
    mark.textContent = paragraph.text.slice(
      highlight.startOffset,
      highlight.endOffset,
    );
    mark.title = `${highlight.color} study highlight`;
    root.append(mark);
    cursor = highlight.endOffset;
  }
  root.append(document.createTextNode(paragraph.text.slice(cursor)));
}

export function createParagraph(
  talkId: string,
  paragraph: Paragraph,
  footnotes: Footnote[],
  initialHighlights: StudyHighlight[],
  initialNote: StudyNote | undefined,
  actions: ParagraphActions,
): ParagraphView {
  const section = document.createElement('section');
  section.className =
    paragraph.kind === 'heading'
      ? 'reader-paragraph reader-paragraph--heading'
      : 'reader-paragraph';
  section.id = paragraph.id;
  section.dataset.paragraphId = paragraph.id;
  section.tabIndex = -1;

  const number = document.createElement('a');
  number.className = 'reader-paragraph__number';
  number.href = `/read/${talkId}/${paragraph.id}`;
  number.dataset.router = '';
  number.setAttribute(
    'aria-label',
    `Link to paragraph ${String(paragraph.index + 1)}`,
  );
  number.textContent = String(paragraph.index + 1);

  const copy =
    paragraph.kind === 'heading'
      ? document.createElement('h2')
      : document.createElement('p');
  copy.className = 'reader-paragraph__copy';
  const textRoot = document.createElement('span');
  textRoot.className = 'reader-paragraph__text';
  copy.append(textRoot);

  for (const footnote of footnotes) {
    copy.append(document.createTextNode(' '));
    const trigger = document.createElement('button');
    trigger.className = 'footnote-trigger';
    trigger.type = 'button';
    trigger.setAttribute(
      'aria-label',
      `Published footnote ${String(footnote.number)}${footnote.label ? `: ${footnote.label}` : ''}`,
    );
    trigger.textContent = String(footnote.number);
    trigger.addEventListener('click', () => {
      actions.onFootnote(footnote, trigger);
    });
    copy.append(trigger);
  }

  const actionBar = document.createElement('div');
  actionBar.className = 'paragraph-actions';
  const highlightButton = document.createElement('button');
  highlightButton.type = 'button';
  highlightButton.textContent = 'Highlight';
  highlightButton.setAttribute(
    'aria-label',
    `Highlight paragraph ${String(paragraph.index + 1)}`,
  );
  highlightButton.addEventListener('click', () => {
    actions.onHighlight(paragraph, selectedText(textRoot, paragraph), highlightButton);
  });

  const noteButton = document.createElement('button');
  noteButton.type = 'button';
  noteButton.textContent = 'Note';
  noteButton.setAttribute(
    'aria-label',
    `Add note to paragraph ${String(paragraph.index + 1)}`,
  );

  const shareButton = document.createElement('button');
  shareButton.type = 'button';
  shareButton.textContent = 'Share';
  shareButton.setAttribute(
    'aria-label',
    `Share paragraph ${String(paragraph.index + 1)}`,
  );
  shareButton.addEventListener('click', () => {
    actions.onShare(paragraph, shareButton);
  });
  actionBar.append(highlightButton, noteButton, shareButton);
  section.append(number, copy, actionBar);

  let note = initialNote;
  const updateStudyState = (
    highlights: StudyHighlight[],
    nextNote: StudyNote | undefined,
  ): void => {
    note = nextNote;
    renderHighlightedText(textRoot, paragraph, highlights);
    noteButton.classList.toggle('has-note', Boolean(note));
    noteButton.textContent = note ? 'Edit note' : 'Note';
    noteButton.setAttribute(
      'aria-label',
      `${note ? 'Edit' : 'Add'} note for paragraph ${String(paragraph.index + 1)}`,
    );
  };
  noteButton.addEventListener('click', () => {
    actions.onNote(paragraph, note, noteButton);
  });
  updateStudyState(initialHighlights, initialNote);

  return { element: section, updateStudyState };
}
